// react
import { useState } from 'react';

// third-party
import { LoadingButton } from '@mui/lab';
import { Autocomplete, Button, CardContent, DialogTitle, Divider, InputLabel, Stack, TextField } from '@mui/material';
import { Form, FormikProvider, useFormik } from 'formik';
import * as yup from 'yup';

// project import
import VenueDto from 'common/dto-types/venue-dto';
import RequiredAsterisk from 'components/RequiredAsterisk';
import { dispatch, useSelector } from 'store';
import { closeForm } from 'store/reducers/venue';
import { mapCountryToSelectOption } from '../../common/dto-types/country-dto';
import SelectAsyncFormField from '../../components/select-async-form-field';
import { openSnackbar } from '../../store/reducers/snackbar';
import { SelectOptionType } from '../../utils/react-table';
import { createVenue, updateVenue } from './api';
import ReactQuillWrapper from 'components/react-quill-wrapper/react-quill-wrapper';
import { ITimezoneOption, useTimezoneSelect } from 'react-timezone-select';
import { mapVenueTypeToSelectOption } from 'common/dto-types/venue-type-dto';
import AsyncSelectSearchFormField from 'components/async-select-search-form-field';
import FDCPServices from 'fdcp/fdcp-services';
import TournamentDto, { mapFdcpSeasonToTournamentDto } from 'common/dto-types/tournament-dto';
import { GET_HOST_CITIES } from 'pages/calendar/api';
import { mapCityToSelectOption } from 'common/dto-types/city-dto';
import { timezones } from 'utils/calendar/timezone-switch';

type VenueFormFields = {
    id: number;
    name: string;
    description: string;
    address: string;
    addressLine: string;
    city: string;
    state: string;
    country: SelectOptionType | null;
    postalCode: string;
    imageUrl: string;
    latitude: number | null;
    longitude: number | null;
    ifesId: string;
    url: string;
    venueType: SelectOptionType;
    timeZone: SelectOptionType;
    tournaments: TournamentDto[];
    hostCity?: SelectOptionType;
};

type VenueFormProps = {
    refreshCallback: () => Promise<void>;
};

const VenueForm = ({ refreshCallback }: VenueFormProps): JSX.Element => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const data = useSelector((state) => state.venue.data);
    const isCreating = !data;

    const convertToVenueDto = (venue: VenueFormFields): VenueDto => {
        const dto: VenueDto = {
            id: venue.id,
            name: venue.name,
            description: venue.description,
            city: venue.city,
            countryId: Number(venue?.country?.value),
            country: venue?.country?.label ?? '',
            address: venue.address,
            addressLine: venue.addressLine,
            state: venue.state,
            postalCode: venue.postalCode,
            imageUrl: venue.imageUrl,
            ifesId: venue.ifesId,
            latitude: venue.latitude,
            longitude: venue.longitude,
            url: venue.url,
            venueTypeId: Number(venue.venueType?.value),
            timeZone: typeof venue.timeZone?.value === 'string' ? venue.timeZone.value : '',
            isVenueAssigned: false,
            canShowInSubscription: false,
            tournaments: venue.tournaments,
            hostCityId: Number(venue.hostCity?.value),
        };

        return dto;
    };

    const { options: timeZoneOptions } = useTimezoneSelect({
        labelStyle: 'original',
        timezones: timezones,
        displayValue: 'UTC',
    });

    const initialValues: VenueFormFields = {
        id: data?.id ?? 0,
        name: data?.name ?? '',
        description: data?.description ?? '',
        address: data?.address ?? '',
        addressLine: data?.addressLine ?? '',
        city: data?.city ?? '',
        state: data?.state ?? '',
        country: {
            value: data?.countryId ?? 0,
            label: data?.country ?? '',
        },
        postalCode: data?.postalCode ?? '',
        imageUrl: data?.imageUrl ?? '',
        latitude: data?.latitude ?? null,
        longitude: data?.longitude ?? null,
        ifesId: data?.ifesId ?? '',
        url: data?.url ?? '',
        venueType: {
            value: data?.venueTypeId ?? 0,
            label: data?.venueType?.name ?? '',
        },
        timeZone: data?.timeZone
            ? (timeZoneOptions.find((option) => option.value === data?.timeZone) as ITimezoneOption)
            : { value: '', label: '' },
        tournaments: data?.tournaments ?? [],
    };

    const validationSchema = yup.object({
        name: yup.string().required('Name is required').max(255, 'Name cannot exceed 255 characters'),
        latitude: yup.number().notRequired(),
        longitude: yup.number().notRequired(),
        country: yup.object().shape({ value: yup.number(), label: yup.string() }).required('Country is required'),
        venueType: yup.object().shape({ value: yup.number(), label: yup.string() }).required('Venue Type is required'),
    });

    const handleVenueSubmit = async (value: VenueFormFields) => {
        setIsLoading(true);
        const dto = convertToVenueDto(value);
        let data: number | null | undefined;
        let successMessage = '';
        let failedMessage = '';

        if (isCreating) {
            data = await createVenue(dto);
            successMessage = 'Venue created successfully!';
            failedMessage = 'Failed to create Venue';
        } else {
            data = await updateVenue(dto);
            successMessage = 'Venue updated successfully!';
            failedMessage = 'Failed to update Venue';
        }

        if (data) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: successMessage,
                    variant: 'alert',
                    alert: {
                        color: 'success',
                    },
                }),
            );
            refreshCallback();
        } else {
            dispatch(
                openSnackbar({
                    open: true,
                    message: failedMessage,
                    variant: 'alert',
                    alert: {
                        color: 'error',
                    },
                }),
            );
        }
        setIsLoading(false);
    };

    const formik = useFormik<VenueFormFields>({
        initialValues,
        validationSchema,
        onSubmit: handleVenueSubmit,
    });

    const { errors, touched, dirty, isValid, getFieldProps, handleSubmit } = formik;

    return (
        <FormikProvider value={formik}>
            <Form onSubmit={handleSubmit}>
                <DialogTitle>{isCreating ? 'Add' : 'Update'} Venue</DialogTitle>
                <CardContent>
                    <Stack spacing={3}>
                        <Stack spacing={1}>
                            <InputLabel>
                                Venue Name <RequiredAsterisk />
                            </InputLabel>
                            <TextField
                                fullWidth
                                id="name"
                                placeholder="Enter Venue Name"
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name}
                                {...getFieldProps('name')}
                            />
                        </Stack>
                        <Stack spacing={1}>
                            <SelectAsyncFormField
                                label="Venue Type"
                                name="venueType"
                                isRequired
                                initialValues={Number(formik.initialValues.venueType?.value) ?? 0}
                                source={'venueTypes'}
                                optionMapperCb={mapVenueTypeToSelectOption}
                            />
                        </Stack>
                        <Stack spacing={1.25}>
                            <InputLabel htmlFor="event-description">Description</InputLabel>
                            <ReactQuillWrapper name="description" minHeight="136px" />
                        </Stack>
                        <Stack spacing={1}>
                            <InputLabel>Address</InputLabel>
                            <TextField
                                fullWidth
                                id="address"
                                placeholder="Enter Address"
                                error={touched.address && Boolean(errors.address)}
                                helperText={touched.address && errors.address}
                                {...getFieldProps('address')}
                            />
                        </Stack>
                        <Stack spacing={1}>
                            <InputLabel>Address Line</InputLabel>
                            <TextField
                                fullWidth
                                id="addressLine"
                                placeholder="Enter Address Line"
                                error={touched.addressLine && Boolean(errors.addressLine)}
                                helperText={touched.addressLine && errors.addressLine}
                                {...getFieldProps('addressLine')}
                            />
                        </Stack>
                        <Stack spacing={1}>
                            <InputLabel>City</InputLabel>
                            <TextField
                                fullWidth
                                id="city"
                                placeholder="Enter City"
                                error={touched.city && Boolean(errors.city)}
                                helperText={touched.city && errors.city}
                                {...getFieldProps('city')}
                            />
                        </Stack>
                        <Stack spacing={1}>
                            <InputLabel>Postal Code</InputLabel>
                            <TextField
                                fullWidth
                                id="postalCode"
                                placeholder="Enter postal code"
                                error={touched.postalCode && Boolean(errors.postalCode)}
                                helperText={touched.postalCode && errors.postalCode}
                                {...getFieldProps('postalCode')}
                            />
                        </Stack>
                        <Stack spacing={1}>
                            <InputLabel>State</InputLabel>
                            <TextField
                                fullWidth
                                id="state"
                                placeholder="Enter state"
                                error={touched.state && Boolean(errors.state)}
                                helperText={touched.state && errors.state}
                                {...getFieldProps('state')}
                            />
                        </Stack>
                        <Stack spacing={1}>
                            <SelectAsyncFormField
                                label="Country"
                                name="country"
                                isRequired
                                initialValues={Number(formik.initialValues.country?.value) ?? 0}
                                source={'countries'}
                                optionMapperCb={mapCountryToSelectOption}
                            />
                        </Stack>
                        <Stack spacing={1}>
                            <InputLabel>Time Zone</InputLabel>
                            <Autocomplete
                                id="timeZone"
                                options={timeZoneOptions}
                                onChange={(_, newValue) => {
                                    formik.setFieldValue('timeZone', newValue);
                                }}
                                value={formik.values.timeZone}
                                getOptionLabel={(option) => option.label}
                                renderInput={(params) => <TextField {...params} />}
                                isOptionEqualToValue={(option, selectedValue) => {
                                    return option.value === selectedValue.value;
                                }}
                            />
                        </Stack>
                        <Stack spacing={1}>
                            <AsyncSelectSearchFormField
                                label="Tournaments"
                                name="tournaments"
                                initialValues={initialValues?.tournaments ?? []}
                                fetchData={(inputValue) =>
                                    FDCPServices.searchSeasonByName({ searchString: inputValue }).then((seasons) =>
                                        seasons.map((season) => mapFdcpSeasonToTournamentDto(season)),
                                    )
                                }
                                getOptionLabel={(option) => `${option.name} - [${option.fdcpId}]`}
                                isOptionEqualToValue={(option, selectedValue) => {
                                    return option.fdcpId === selectedValue.fdcpId;
                                }}
                                filterOptions={(x) => x}
                                multiple
                            />
                        </Stack>
                        <Stack spacing={1}>
                            <SelectAsyncFormField
                                label="Host City"
                                name="hostCity"
                                initialValues={data?.hostCityId}
                                source={`${GET_HOST_CITIES}`}
                                optionMapperCb={mapCityToSelectOption}
                            />
                        </Stack>
                        <Stack spacing={1}>
                            <InputLabel>Image URL</InputLabel>
                            <TextField
                                fullWidth
                                id="imageUrl"
                                placeholder="Enter Image URL"
                                error={touched.imageUrl && Boolean(errors.imageUrl)}
                                helperText={touched.imageUrl && errors.imageUrl}
                                {...getFieldProps('imageUrl')}
                            />
                        </Stack>
                        <Stack spacing={1}>
                            <InputLabel>Latitude</InputLabel>
                            <TextField
                                fullWidth
                                id="latitude"
                                placeholder="Enter Latitude"
                                error={touched.latitude && Boolean(errors.latitude)}
                                helperText={touched.latitude && errors.latitude}
                                {...getFieldProps('latitude')}
                            />
                        </Stack>
                        <Stack spacing={1}>
                            <InputLabel>Longitude</InputLabel>
                            <TextField
                                fullWidth
                                id="longitude"
                                placeholder="Enter Longitude"
                                error={touched.longitude && Boolean(errors.longitude)}
                                helperText={touched.longitude && errors.longitude}
                                {...getFieldProps('longitude')}
                            />
                        </Stack>
                        <Stack spacing={1}>
                            <InputLabel>Ifes Id</InputLabel>
                            <TextField
                                fullWidth
                                id="ifesId"
                                placeholder="Enter Ifes Id"
                                error={touched.ifesId && Boolean(errors.ifesId)}
                                helperText={touched.ifesId && errors.ifesId}
                                {...getFieldProps('ifesId')}
                            />
                        </Stack>
                        <Stack spacing={1}>
                            <InputLabel>URL</InputLabel>
                            <TextField
                                fullWidth
                                id="url"
                                placeholder="Enter URL"
                                error={touched.url && Boolean(errors.url)}
                                helperText={touched.url && errors.url}
                                {...getFieldProps('url')}
                            />
                        </Stack>
                    </Stack>
                </CardContent>
                <Divider />
                <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ px: 2.5, py: 2 }}>
                    <Button color="error" size="small" disabled={isLoading} onClick={() => dispatch(closeForm())}>
                        Cancel
                    </Button>
                    <LoadingButton
                        type="submit"
                        variant="contained"
                        size="small"
                        loading={isLoading}
                        disabled={!isValid || (isCreating && !dirty)}
                    >
                        Save
                    </LoadingButton>
                </Stack>
            </Form>
        </FormikProvider>
    );
};

export default VenueForm;
